import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { ALGO_RISK_TRADE_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { Injectable } from "@angular/core";
import { Optional } from "ag-grid-community";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import {
  SystemConfigPromiseService,
  BfxSysConfigUtilService,
  BfxConfigService,
} from "../../auth-services";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { TranslateService } from "@ngx-translate/core";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { BlotterType } from "shared/cubiq-model-multi-asset";
import { BfxColDef, TableDefinition } from "./TableDefinition";
import { RiskTradeTableDefinitionFactory } from "shared/cubiq-services/model/RiskTradeTableDefinitionFactory";
import {
  COLUMN_ACCOUNT_CODE,
  COLUMN_ACCOUNT_NAME,
  COLUMN_AGGREGATE_DEAL_ID,
  COLUMN_BACK_OFFICE_IDS,
  COLUMN_BM_FIXING_TIME,
  COLUMN_BM_FIXING_TIME_UTC,
  COLUMN_CLIENT_ALLIN,
  COLUMN_CLIENT_BRANCH,
  COLUMN_CLIENT_CODE,
  COLUMN_CLIENT_COMMENT,
  COLUMN_CLIENT_NAME,
  COLUMN_CLIENT_PTS,
  COLUMN_CLIENT_SPOT,
  COLUMN_CLORDERID,
  COLUMN_CONFIRMED_BY,
  COLUMN_COUNTER_AMT,
  COLUMN_COUNTER_CURRENCY,
  COLUMN_DEALT_AMT,
  COLUMN_DEALT_AMT_USD,
  COLUMN_DEALT_CCY,
  COLUMN_DEAL_ID,
  COLUMN_DEAL_STATE,
  COLUMN_DIR,
  COLUMN_ENTRY_DATE,
  COLUMN_ENTRY_USER_ID,
  COLUMN_EXTERNAL_PRIMARY_EXEC_ID,
  COLUMN_EXTERNAL_SECONDARY_EXEC_ID,
  COLUMN_FILL_COMMENT,
  COLUMN_FILL_DATE,
  COLUMN_FILL_STATE,
  COLUMN_FILL_USER_CODE,
  COLUMN_FILL_USER_NAME,
  COLUMN_FIXING_DATE,
  COLUMN_FWD_PORTFOLIO,
  COLUMN_INSTRUMENT,
  COLUMN_LAST_MARKET,
  COLUMN_MARKET_ALLIN,
  COLUMN_MARKET_PTS,
  COLUMN_MARKET_SPOT,
  COLUMN_MARKUP,
  COLUMN_OBO_ACCOUNT,
  COLUMN_OBO_CLIENT,
  COLUMN_OBO_ENTRY_USER,
  COLUMN_ORDER_ID,
  COLUMN_PRIVATE_COMMENT,
  COLUMN_REQUEST_RATE,
  COLUMN_SPOT_PORTFOLIO,
  COLUMN_STRATEGY_ID,
  COLUMN_TRADER_GROUP_ID,
  COLUMN_TRADE_CLIENT_TRADER,
  COLUMN_TRADE_DATE,
  COLUMN_TRADE_FIXING_SOURCE_CODE,
  COLUMN_TRADE_ORDER_PRODUCT,
  COLUMN_TRADE_ORDER_TYPE,
  COLUMN_TRADE_SALES_GROUP_ID,
  COLUMN_TRADE_VALUE_DATE,
} from "./TradeTableDefinitionFactory.constants";

const CUSTOM_PLACEHOLDER: string = "CUSTOM_PLACEHOLDER";

const EXCLUDED_FIELDS: string[] = [COLUMN_CONFIRMED_BY];

const ALGO_RISK_TRADE_COL_ORDER: string[] = [
  COLUMN_FILL_DATE, //   Fill Date
  COLUMN_DEAL_ID, // 2	Deal ID
  COLUMN_AGGREGATE_DEAL_ID, // 3	Aggregate Deal ID
  COLUMN_ORDER_ID, // 4	Order ID
  COLUMN_CLIENT_NAME, // 5	Client Name
  COLUMN_CLIENT_CODE, // 6	Client ID
  COLUMN_ACCOUNT_NAME, // 7	Account Name
  COLUMN_ACCOUNT_CODE, // 8	Account ID
  COLUMN_DIR, // 9	Dir
  COLUMN_DEALT_CCY, // 10	Dealt Ccy

  COLUMN_DEALT_AMT, // 11	Dealt Amt
  COLUMN_DEALT_AMT_USD, // 12	Dealt Amt (USD)
  COLUMN_COUNTER_CURRENCY, // 13	Counter Ccy
  COLUMN_COUNTER_AMT, // 14	Counter Amt
  COLUMN_INSTRUMENT, // 15	Instrument
  COLUMN_MARKET_SPOT, // 16	Market Spot
  COLUMN_CLIENT_SPOT, // 17	Client Spot
  COLUMN_MARKET_PTS, // 18	Market Pts
  COLUMN_CLIENT_PTS, // 19	Client Pts
  COLUMN_MARKET_ALLIN, // 20	Market All-In
  COLUMN_CLIENT_ALLIN, // 21	Client All-in
  COLUMN_MARKUP, // 22	Markup
  COLUMN_TRADE_DATE, // 23	Trade Date
  COLUMN_TRADE_VALUE_DATE, // 24	Value Date
  COLUMN_TRADER_GROUP_ID, // 25	Trader Group
  COLUMN_LAST_MARKET, // 26	Last Market
  COLUMN_EXTERNAL_PRIMARY_EXEC_ID, // 27	External Primary Exec ID
  COLUMN_EXTERNAL_SECONDARY_EXEC_ID, // 28	External Secondary Exec ID
  COLUMN_TRADE_ORDER_TYPE, // 29	Order Type
  COLUMN_TRADE_FIXING_SOURCE_CODE, // 30	Fixing Code
  COLUMN_FIXING_DATE, // 31	Fixing Date
  COLUMN_BM_FIXING_TIME, // 32	Fixing Time
  COLUMN_BM_FIXING_TIME_UTC, // 33	Fixing UTC Time
  COLUMN_TRADE_ORDER_PRODUCT, // 34	Product
  COLUMN_CLIENT_COMMENT, // 35	Client Comments
  COLUMN_PRIVATE_COMMENT, // 36	Private Comments
  COLUMN_ENTRY_USER_ID, // 37	Entry User
  COLUMN_ENTRY_DATE, // 38	Entry Date
  COLUMN_STRATEGY_ID, // 39	Strategy ID
  COLUMN_CLORDERID, // 40	ClOrderId
  COLUMN_DEAL_STATE, // 41	Deal State
  COLUMN_FILL_USER_NAME, // 42	Fill User
  COLUMN_FILL_USER_CODE, // 42	Fill User
  COLUMN_TRADE_CLIENT_TRADER, // 43	Client Trader
  COLUMN_CLIENT_BRANCH, // 44	Client Branch
  COLUMN_FILL_COMMENT, // 45	Fill Comments
  COLUMN_OBO_CLIENT, // 46	OBO Client
  COLUMN_OBO_ACCOUNT, // 47	OBO Account
  COLUMN_OBO_ENTRY_USER, // 48	OBO Entry User
  COLUMN_BACK_OFFICE_IDS, //Booking Id (Back Office Id)column?
  COLUMN_TRADE_SALES_GROUP_ID, // Sales Group?
  COLUMN_SPOT_PORTFOLIO, // Spot Portfolio?
  COLUMN_FWD_PORTFOLIO, // Forward portfolio?
  COLUMN_REQUEST_RATE, // Request Rate
  COLUMN_FILL_STATE, // Fill State (Status)
  CUSTOM_PLACEHOLDER, // All the custom fields grouped together
];

const ALGO_RISK_TRADE_DEF_COLS: string[] = [
  COLUMN_FILL_DATE, //   Fill Date
  COLUMN_DEAL_ID, // 2	Deal ID
  COLUMN_AGGREGATE_DEAL_ID, // 3	Aggregate Deal ID
  COLUMN_ORDER_ID, // 4	Order ID
  COLUMN_CLIENT_NAME, // 5	Client Name
  COLUMN_DIR, // 9	Dir
  COLUMN_DEALT_CCY, // 10	Dealt Ccy

  COLUMN_DEALT_AMT, // 11	Dealt Amt
  COLUMN_INSTRUMENT, // 15	Instrument
  COLUMN_MARKET_SPOT, // 16	Market Spot
  COLUMN_CLIENT_SPOT, // 17	Client Spot
  COLUMN_MARKET_PTS, // 18	Market Pts
  COLUMN_CLIENT_PTS, // 19	Client Pts
  COLUMN_MARKET_ALLIN, // 20	Market All-In
  COLUMN_CLIENT_ALLIN, // 21	Client All-in
  COLUMN_TRADE_VALUE_DATE, // 24	Value Date
  COLUMN_TRADER_GROUP_ID, // 25	Trader Group
  COLUMN_LAST_MARKET, // 26	Last Market
  COLUMN_EXTERNAL_PRIMARY_EXEC_ID, // 27	External Primary Exec ID
  COLUMN_EXTERNAL_SECONDARY_EXEC_ID, // 28	External Secondary Exec ID
];
/**
 * Currently an exact copy of othe RiskTrade Table but with a different type as these configs
 * need to be kept separate.
 * In future this may differ more substantially so I kept them separate
 */
@Injectable()
export class AlgoRiskTradeTableDefinitionFactory extends RiskTradeTableDefinitionFactory {
  constructor(
    profileService: ProfilePromiseActionService,
    atdlTooltipUiRep: AtdlTooltipUiRep,
    // It is not possible to inject against an interface
    @Optional() _ccyLookupService: CcyLookupService,
    @Optional() _ccyPairLookupService: CcyPairLookupService,
    @Optional() _clientLookup: ClientLookupService,
    @Optional() _accountLookup: AccountLookupService,
    @Optional() _salesGroupLookup: SalesGroupLookupService,
    @Optional() _traderGroupLookup: TraderGroupLookupService,
    @Optional() _userLookup: UserLookupService,
    @Optional() _sysConfigService: SystemConfigPromiseService,
    @Optional() _translateService: TranslateService,
    @Optional() algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional() cmp: ClientModelPrivatization,
    @Optional() amp: AccountModelPrivatization,
    @Optional() ctmp: ClientTraderModelPrivatization,
    @Optional() sysConfigUtil: BfxSysConfigUtilService,
    @Optional() configService: BfxConfigService,
    @Optional() customFieldLookupService: CustomFieldLookupService,
  ) {
    super(
      profileService,
      atdlTooltipUiRep,
      _ccyLookupService,
      _ccyPairLookupService,
      _clientLookup,
      _accountLookup,
      _salesGroupLookup,
      _traderGroupLookup,
      _userLookup,
      _sysConfigService,
      _translateService,
      algoStrategyLookupService,
      cmp,
      amp,
      ctmp,
      sysConfigUtil,
      configService,
      customFieldLookupService,
    );

    this.statusIconHelper.ignoreSTPDisabledIcons = true;
  }

  public override createDef(): TableDefinition {
    let tableDefinition = super.createDef();

    tableDefinition.riskTradeTableDefinition = true;

    EXCLUDED_FIELDS.forEach((c) => tableDefinition.removeColumnDefinition(c));
    this.reorderColumns(tableDefinition);

    return tableDefinition;
  }

  /**
   * If columns have not been restored from saved config, then reorder and update visibility
   * based on the ordering and visibility constants
   *
   * @param tableDefinition
   */
  reorderColumns(tableDefinition: TableDefinition) {
    let currCol: number = 0;

    ALGO_RISK_TRADE_COL_ORDER.forEach((col) => {
      if (CUSTOM_PLACEHOLDER === col) {
        // For custom fields, we must set them in single block
        this.customFieldLookupService.getAllCustomFields().forEach((item) => {
          if (item.isEnabledFg) {
            let done: boolean = this.setColPosition(tableDefinition, item.fieldId, currCol);
            if (done) {
              currCol = currCol + 1;
            }
          }
        });
      } else {
        let done: boolean = this.setColPosition(tableDefinition, col, currCol);
        if (done) {
          currCol = currCol + 1;
        }
      }
    });
  }

  /**
   * If column has not been restored, then set position and visibility based on local
   * constants
   *
   * @param tableDefinition
   * @param col
   * @param currCol
   * @returns
   */
  setColPosition(tableDefinition: TableDefinition, col: string, currCol: number) {
    let ret: boolean = false;
    let colIndex = tableDefinition.columnIndexMap.get(col);
    if (null != colIndex) {
      let colDef: BfxColDef = tableDefinition.columns.at(colIndex);
      const alreadyHasConfig: boolean = this.restoreFromConfig(colDef);

      if (!alreadyHasConfig && null != colDef) {
        colDef.position = currCol;

        const visibleByDefault: boolean = ALGO_RISK_TRADE_DEF_COLS.includes(col);
        colDef.hide = !visibleByDefault;
        ret = true;
      }
    }
    return ret;
  }

  public override getVersionIdentifier(): string {
    return ALGO_RISK_TRADE_TABLE_DEFINITION_VER;
  }

  public override getBlotterType(): BlotterType {
    return BlotterType.ALGO_RISK_TRADE;
  }
}
